import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ContactInformation, ContactInformationType, ContactpersonalOverviewModel, CustomerOverviewModel, PaginationPage, PaginationResponse, SearchObject } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { TokenService } from '../../shared/services/token.service';
import { Contact } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class ContactService {

  private _contacts: { [key: number]: ReplaySubject<Contact> } = {};
  contacts$ = combineLatest(Object.values(this._contacts));

  private _contactInformationTypes = new BehaviorSubject<ContactInformationType[]>(null);
  contactInformationTypes$ = this._contactInformationTypes.pipe(switchMap((x: ContactInformationType[]) => {
    if (Array.isArray(x)) return of(x);
    let subject = new Subject();
    this._getContactInformationTypes().subscribe({
      next: (result) => {
        this._contactInformationTypes.next(result);
        subject.next(result);
      },
      error: (error) => {
        this._contactInformationTypes.error(error);
        subject.error(error);
      }
    });
    return subject.asObservable();
  }));

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService
  ) { }

  checkContactVersion(contact: Contact): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/contact/${contact.contactpersid}/check-version/${contact.concurrencystamp}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  clearContactHistory = () => this._httpClient.delete(`${serverLocation}/api/contactpersonal/history`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  });

  contactIsWatched(contactId: number): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/contact/${contactId}/watched`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  createContact(contact: Contact, note: string = null): Observable<Contact> {
    return this._httpClient.post(`${serverLocation}/api/contactpersonal`, {
      content: contact,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contact>;
  }

  createContactInformation(information: ContactInformation) {
    return this._httpClient.post(`${serverLocation}/api/contact-information`, information, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contact>;
  }

  deleteContact(contactId: number): Observable<Object> {
    let subject = new Subject();
    this._deleteContactById(contactId).pipe(tap(() => {
      if (this._contacts[contactId]) {
        this._contacts[contactId].complete();
        delete this._contacts[contactId];
      }
    })).subscribe(subject);
    return subject;
  }

  deleteContactInformation(infoId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/contact-information/${infoId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  generateNumber(){
    return this._httpClient.get(`${serverLocation}/api/contactpersonal/generate-number`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  getContactById(identifier: number, soft: boolean = true): Observable<Contact> {
    if (typeof identifier !== 'number') return of(null);
    if (!soft || !this._contacts[identifier]) {
      if (!this._contacts[identifier]) this._contacts[identifier] = new ReplaySubject(1);
      this._getContactById(identifier).subscribe({
        next: (contact: Contact) => this._contacts[identifier].next(contact),
        error: (error: HttpErrorResponse) => this._contacts[identifier].error(error)
      });
    }
    return this._contacts[identifier].asObservable();
  }

  getCustomerContacts(customerId: number): Observable<Contact[]> {
    return this._httpClient.get(`${serverLocation}/api/customer/${customerId}/contacts`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contact[]>;
  }

  getContactCustomers(contactId: number): Observable<CustomerOverviewModel[]> {
    return this._httpClient.get(`${serverLocation}/api/contactpersonal/${contactId}/customers`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<CustomerOverviewModel[]>;
  }

  getDepartmentSuggestions(page: PaginationPage = { 'pageNumber': 1, 'pageSize': 20 }): Observable<PaginationResponse<string[]>> {
    return this._httpClient.post(`${serverLocation}/api/contactpersonal/departments`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<string[]>>;
  }

  getWatchedContacts(page: PaginationPage): Observable<PaginationResponse<ContactpersonalOverviewModel[]>> {
    return this._httpClient.post(`${serverLocation}/api/contactpersonal/watched`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<ContactpersonalOverviewModel[]>>;
  }

  getUserContactHistory(page: PaginationPage): Observable<PaginationResponse<ContactpersonalOverviewModel[]>> {
    return this._httpClient.post(`${serverLocation}/api/contactpersonal/history`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<ContactpersonalOverviewModel[]>>;
  }

  saveHistory(customerId: number): Observable<Object> {
    let subject = new Subject();
    this._httpClient.get(`${serverLocation}/api/contactpersonal/history/${customerId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }).subscribe(subject);
    return subject.asObservable();
  }

  searchContacts(search: SearchObject): Observable<ContactpersonalOverviewModel[]> {
    return this._httpClient.post(`${serverLocation}/api/contactpersonal/search`, search, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContactpersonalOverviewModel[]>;
  }

  searchContactsByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/contactpersonal/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<ContactpersonalOverviewModel[]>>;
  }

  searchContactsByUserFilter(identifier: number, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/contactpersonal/user-filter-search/${identifier}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<ContactpersonalOverviewModel[]>>;
  }

  setContactWatchedStatus(contactId: number, watch: boolean): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/contactpersonal/${contactId}/${watch? 'watch': 'unwatch'}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  updateContact(contact: Contact, note: string = null): Observable<Contact> {
    return this._httpClient.put(`${serverLocation}/api/contactpersonal`, {
      content: contact,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contact>;
  }

  updateContactInformation(information: ContactInformation) {
    return this._httpClient.put(`${serverLocation}/api/contact-information`, information, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contact>;
  }

  private _deleteContactById(contactId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/contactpersonal/${contactId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  private _getContactById(identifier: number): Observable<Contact> {
    return this._httpClient.get(`${serverLocation}/api/contactpersonal/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contact>;
  }

  private _getContactInformationTypes(): Observable<ContactInformationType[]> {
    return this._httpClient.get(`${serverLocation}/api/contact-information-type`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContactInformationType[]>;
  }
}
